import { z } from 'zod';
import { COMMISSION_TIERS, RESERVATION_STATUSES, ROLES, SALESPERSON_TIERS, VEHICLE_CLASSES, VEHICLE_STATUSES } from './types';

const emptyToUndefined = (value: unknown) => value === '' ? undefined : value;
const optionalNumber = (schema: z.ZodType<number, unknown>) => z.preprocess(emptyToUndefined, schema.optional());
const unknownToNull = (value: unknown) => value === '' || value === undefined ? null : value;
const rpDescription = (value: string) => value.replace(/\b(?:Grand Theft Auto|GTA)(?:\s+(?:V|5|Online))?\b/giu, 'Los Santos');

export const vehicleQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(24),
  search: z.string().trim().max(100).optional(),
  brand: z.string().trim().max(80).optional(),
  category: z.string().trim().max(80).optional(),
  minPrice: optionalNumber(z.coerce.number().min(0)),
  maxPrice: optionalNumber(z.coerce.number().min(0)),
  minHp: optionalNumber(z.coerce.number().min(0)),
  maxHp: optionalNumber(z.coerce.number().min(0)),
  minSpeed: optionalNumber(z.coerce.number().min(0)),
  maxSpeed: optionalNumber(z.coerce.number().min(0)),
  traction: z.enum(['FWD', 'RWD', 'AWD']).optional(),
  transmission: z.enum(['Manual', 'Automática', 'Secuencial']).optional(),
  fuel: z.enum(['Gasolina', 'Diésel', 'Eléctrico', 'Híbrido']).optional(),
  seats: optionalNumber(z.coerce.number().int().min(1).max(20)),
  doors: optionalNumber(z.coerce.number().int().min(1).max(10)),
  vehicleClass: z.enum(VEHICLE_CLASSES).optional(),
  status: z.enum(VEHICLE_STATUSES).optional(),
  inStock: z.enum(['true', 'false']).optional(),
  featured: z.enum(['true', 'false']).optional(),
  sale: z.enum(['true', 'false']).optional(),
  sort: z.enum(['price_asc', 'price_desc', 'newest', 'oldest', 'horsepower', 'top_speed', 'brand_asc', 'brand_desc', 'stock', 'popular']).default('newest'),
});

export const vehicleInputSchema = z.object({
  slug: z.string().trim().regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/).max(120),
  brand: z.string().trim().min(1).max(80),
  model: z.string().trim().min(1).max(80),
  display_name: z.string().trim().min(1).max(160),
  description: z.string().trim().min(10).max(4000).transform(rpDescription),
  category_id: z.coerce.number().int().positive(),
  price: z.coerce.number().int().min(0).max(2_000_000_000),
  original_price: z.coerce.number().int().min(0).max(2_000_000_000).nullable().default(null),
  sale_price: z.coerce.number().int().min(0).max(2_000_000_000).nullable().default(null),
  discount_percentage: z.coerce.number().int().min(0).max(100).default(0),
  on_sale: z.boolean().default(false),
  sale_start: z.string().datetime().nullable().default(null),
  sale_end: z.string().datetime().nullable().default(null),
  year: z.coerce.number().int().min(1900).max(2100),
  engine: z.string().trim().min(1).max(120),
  horsepower: z.coerce.number().int().min(0).max(5000),
  top_speed: z.coerce.number().int().min(0).max(1000),
  acceleration: z.coerce.number().min(0).max(100),
  weight: z.coerce.number().int().min(1).max(100000),
  traction: z.enum(['FWD', 'RWD', 'AWD']),
  transmission: z.enum(['Manual', 'Automática', 'Secuencial']),
  fuel_type: z.enum(['Gasolina', 'Diésel', 'Eléctrico', 'Híbrido']),
  doors: z.preprocess(unknownToNull, z.coerce.number().int().min(1).max(10).nullable()),
  seats: z.preprocess(unknownToNull, z.coerce.number().int().min(1).max(20).nullable()),
  technical_specs_known: z.boolean().default(false),
  stock_total: z.coerce.number().int().min(0).max(100000),
  status: z.enum(VEHICLE_STATUSES),
  vehicle_class: z.enum(VEHICLE_CLASSES).nullable().default(null),
  featured: z.boolean().default(false),
  handling_score: z.coerce.number().int().min(0).max(100),
  acceleration_score: z.coerce.number().int().min(0).max(100),
  braking_score: z.coerce.number().int().min(0).max(100),
  speed_score: z.coerce.number().int().min(0).max(100),
});

export const reservationInputSchema = z.object({
  vehicleId: z.coerce.number().int().positive(),
  characterName: z.string().trim().min(2).max(100),
  stateId: z.string().trim().min(1).max(40).regex(/^[A-Za-z0-9_-]+$/),
  phone: z.string().trim().min(3).max(30).regex(/^[0-9+() -]+$/),
  comments: z.string().trim().max(1000).optional(),
});

export const reservationLookupSchema = z.object({ code: z.string().trim().toUpperCase().regex(/^GC-RSV-[A-F0-9]{6}$/), stateId: z.string().trim().min(1).max(40) });
export const reservationUpdateSchema = z.object({ status: z.enum(RESERVATION_STATUSES), expectedStatus: z.enum(RESERVATION_STATUSES).optional(), expiresAt: z.string().datetime().nullable().optional(), notes: z.string().max(1000).optional() });
export const stockUpdateSchema = z.object({ mode: z.enum(['increment', 'set']), quantity: z.coerce.number().int().min(-100000).max(100000), reason: z.string().trim().min(3).max(300) });
export const loginSchema = z.object({ username: z.string().trim().min(3).max(80), password: z.string().min(10).max(200) });
export const bootstrapSchema = loginSchema.extend({ displayName: z.string().trim().min(2).max(100) });
export const employeePhoneSchema = z.string().trim().min(3).max(30).regex(/^[0-9+() -]+$/).nullable();
export const employeeInputSchema = bootstrapSchema.extend({ role: z.enum(ROLES), commissionTier: z.enum(SALESPERSON_TIERS).nullable().optional(), phone: employeePhoneSchema.optional() });
export const commissionSettingsSchema = z.object({ rates: z.array(z.object({ tier: z.enum(COMMISSION_TIERS), label: z.string().trim().min(1).max(40), ratePercent: z.coerce.number().min(0).max(100) })).length(4) });
export const pricingSettingsSchema = z.object({
  upchargePercent: z.coerce.number().min(0).max(1000),
  classUpcharges: z.partialRecord(z.enum(VEHICLE_CLASSES), z.coerce.number().min(0).max(1000)).optional(),
  taxPercent: z.coerce.number().min(0).max(100),
  wholesaleTaxPercent: z.coerce.number().min(0).max(100),
  saleTaxPercent: z.coerce.number().min(0).max(100),
  reservationsEnabled: z.boolean(),
});
export const customerInputSchema = z.object({ stateId: z.string().trim().min(1).max(40), name: z.string().trim().min(2).max(100), phone: z.string().trim().min(3).max(30), notes: z.string().trim().max(2000).optional() });
export const saleInputSchema = z.object({ vehicleId: z.coerce.number().int().positive(), customerId: z.coerce.number().int().positive(), reservationId: z.coerce.number().int().positive().nullable().optional(), salePrice: z.coerce.number().int().min(0).max(2_000_000_000) });
